import { Check, ChevronRight, Clock, RefreshCw, TrendingUp } from 'lucide-react';
import Link from 'next/link';
import { useTranslation } from '../../../lib/i18n';
import { useBasePath } from '../../../lib/use-base-path';
import { routeLink } from '../../../lib/route-link';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useWorkspaceGoals } from '../../../lib/goals';
import { GOAL_STATUS_BADGE } from '../../../lib/goal-status';
import { Card, IconCountBadge, IconCountBadges, Typography } from '../ui';
import { AgentEntryRow } from '../agents/AgentEntryRow';
import styles from '../tasks/tasks.module.css';

// The compact Goals summary tile — the Goals counterpart of `TasksSummary`, same shape: a bare "Goals"
// heading, a row of 12px icon-count badges (one per live status, Failed has no goal analogue), and the
// drill-in chevron. The whole card opens `/goals` — account-wide from Your Agents, pre-filtered to one
// Space from that Space's Dashboard (`workspaceId`/`workspaceSlug`).
//
// Each status gets ITS OWN glyph — the badge drops its text label to fit on a 249px tile (`IconCountBadge`),
// so the icon is the only thing telling two statuses apart. Matches the same glyphs `TasksSummary` uses for
// the equivalent status family (waiting-on-you → Clock, in-progress → RefreshCw, done → Check).
const CATS = [
  { key: 'KICKOFF' as const, icon: Clock },
  { key: 'ACTIVE' as const, icon: RefreshCw },
  { key: 'ACHIEVED' as const, icon: Check },
];

export function GoalsSummary({
  entryRow = false,
  workspaceId,
  workspaceSlug,
  pageName = 'agents',
  onSelect,
}: {
  /** Mobile Your Agents: render the compact single-row entry tile, instead of the desktop
   *  heading-over-chips summary. */
  entryRow?: boolean;
  /** Scopes the read to one Space (a Space's Dashboard tab). Omit for the account-wide count. */
  workspaceId?: string;
  /** Carried into the link so the Goals screen opens pre-filtered to that Space. */
  workspaceSlug?: string;
  pageName?: string;
  /** Under the map home the tile is a TAB: this runs instead of navigating (the href stays for cmd-click). */
  onSelect?: () => void;
} = {}) {
  const { t } = useTranslation();
  const basePath = useBasePath();
  const { logPageClickBi } = useGenericBi();
  const state = useWorkspaceGoals(workspaceId);

  if (state.status !== 'ready') return null;
  const counts = { KICKOFF: 0, ACTIVE: 0, ACHIEVED: 0 } as Record<'KICKOFF' | 'ACTIVE' | 'ACHIEVED', number>;
  for (const goal of state.goals) {
    if (goal.status in counts) counts[goal.status as keyof typeof counts]++;
  }
  const total = counts.KICKOFF + counts.ACTIVE + counts.ACHIEVED;
  const empty = total === 0;
  const shown = CATS.filter((c) => counts[c.key] > 0);

  const href = workspaceSlug ? `/goals?workspace=${encodeURIComponent(workspaceSlug)}` : '/goals';
  const openGoals = (e?: { preventDefault: () => void; metaKey?: boolean; ctrlKey?: boolean; shiftKey?: boolean; button?: number }) => {
    // A modifier click keeps the link's own behaviour (a new tab); a plain click is the tab.
    if (onSelect && !(e?.metaKey || e?.ctrlKey || e?.shiftKey || (e?.button ?? 0) !== 0)) {
      e?.preventDefault();
      onSelect();
    }
    return logPageClickBi({ page_name: pageName, button_name: 'open_goals' });
  };

  if (entryRow) {
    return (
      <AgentEntryRow
        title={t('workspace.nav.goals')}
        href={href}
        onClick={openGoals}
        testId="agentsGoalsSummary"
        trailing={
          <IconCountBadges>
            {shown.map((c) => {
              const badge = GOAL_STATUS_BADGE[c.key];
              return (
                <IconCountBadge
                  key={c.key}
                  icon={c.icon}
                  count={counts[c.key]}
                  variant={badge.variant}
                  label={`${counts[c.key]} ${t(badge.labelKey)}`}
                />
              );
            })}
          </IconCountBadges>
        }
      />
    );
  }

  return (
    <Card component={Link} interactive size="sm" className={styles.summaryCard} {...routeLink(basePath, href)} onClick={openGoals}>
      <div className={styles.summaryBody}>
        <span className={styles.summaryTitle}>
          <TrendingUp size={16} aria-hidden />
          <Typography variant="h4" color="primary" component="span" noWrap className={styles.summaryTitleText}>
            {t('workspace.nav.goals')}
          </Typography>
        </span>
        {empty ? (
          <Typography variant="p2" color="secondary">
            {t('goals.empty.title')}
          </Typography>
        ) : (
          <div className={styles.summaryChips}>
            {shown.map((c) => {
              const badge = GOAL_STATUS_BADGE[c.key];
              return (
                <IconCountBadge
                  key={c.key}
                  icon={c.icon}
                  count={counts[c.key]}
                  variant={badge.variant}
                  label={`${counts[c.key]} ${t(badge.labelKey)}`}
                />
              );
            })}
          </div>
        )}
      </div>
      <ChevronRight size={16} className={`${styles.summaryChevron} dir-flip`} aria-hidden />
    </Card>
  );
}
